import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate, Link, useLocation } from 'react-router-dom';
import './../Login.css';
import etiStaakam from '../assets/etiStaakam.png';
import blue_rock_icon from '../assets/blue_rock_icon.png';
import PaperTrade_logo from '../assets/PaperTrade_logo.png';
import learnFNO_small_t from '../assets/learnFNO_small_t.png';
import money_mingle from '../assets/moneyMingle.png';
import triqutra_small from '../assets/Triqutra_small.png';
import reddyannafx_logo from '../assets/RAFX_logo.png';
import deltaFX_logo from '../assets/deltaFX_logo.png';
import { storeToken, consumeAuthMessage } from '../utils/auth';
import { apiAppFetch } from '../utils/apiFetch';

const PersonIcon = () => (
  <svg className="login-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const LockIcon = () => (
  <svg className="login-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

const EyeIcon = ({ open }) => (
  open ? (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
);

function getLogoImage() {
  const urlParams = new URLSearchParams(window.location.search);
  const wlParam = urlParams.get('wl');
  const hostname = window.location.hostname;

  if (hostname.includes('blue') || wlParam === 'blue') return blue_rock_icon;
  if (hostname.includes('learnfno') || wlParam === 'learnfno') return learnFNO_small_t;
  if (hostname.includes('moneymingle') || wlParam === 'moneymingle') return money_mingle;
  if (hostname.includes('triqutra') || wlParam === 'triqutra') return triqutra_small;
  if (hostname.includes('etistaakam') || wlParam === 'etistaakam') return etiStaakam;
  if (hostname.includes('papertrade') || wlParam === 'papertrade') return PaperTrade_logo;
  if (hostname.includes('reddyannafx') || wlParam === 'reddyannafx') return reddyannafx_logo;
  if (hostname.includes('deltafx') || wlParam === 'deltafx') return deltaFX_logo;
  return PaperTrade_logo;
}

function showRegisterLink(hostname) {
  const urlParams = new URLSearchParams(window.location.search);
  return hostname.includes('learnfno') || urlParams.get('wl') === 'learnfno';
}

function safeNextPath(search) {
  const params = new URLSearchParams(search || '');
  const next = String(params.get('next') || '');
  if (!next.startsWith('/') || next.startsWith('//') || next.includes('\\')) {
    return '';
  }
  return next;
}

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [hostname, setHostname] = useState('');
  const [fromLogout, setFromLogout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    const stored = consumeAuthMessage();
    if (stored) {
      return stored;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('session') === 'expired') {
        return 'Session expired. Please login again.';
      }
    } catch (e) {}
    return '';
  });
  const hasCheckedLogout = useRef(false);

  const token = localStorage.getItem('authToken');
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiAppFetch('login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });

      const raw = await response.text();
      let resData;
      try {
        resData = raw ? JSON.parse(raw) : null;
      } catch (parseErr) {
        console.error('Login non-JSON response:', response.status, raw?.slice(0, 200));
        setError(
          `Server returned a non-JSON response (HTTP ${response.status}). Check that api_app.php is deployed on the API host.`
        );
        return;
      }

      if (!resData) {
        setError(`Empty response from server (HTTP ${response.status}).`);
        return;
      }

      if (resData.success) {
        storeToken(resData);

        if (resData.initSetupDone === 0) {
          navigate('/change-password');
          return;
        }

        const next = safeNextPath(location.search);
        navigate(next || `/?appilix_push_notification_user_identity=${resData.clientId}`);
      } else {
        setError(resData.message || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (!navigator.onLine) {
        setError('You appear to be offline. Please check your network and try again.');
      } else if (err?.isNetworkError) {
        setError(
          err.message ||
            'Unable to connect to the API. If this persists, the app domain may not be allowlisted for CORS on the server.'
        );
      } else {
        setError(
          err?.message ||
            'Unable to connect. Please check your network and try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHostname(window.location.hostname);

    if (!hasCheckedLogout.current) {
      const logoutFlag = localStorage.getItem('fromLogout') === 'true';
      setFromLogout(logoutFlag);
      if (logoutFlag) {
        localStorage.removeItem('fromLogout');
      }
      hasCheckedLogout.current = true;
    }
  }, []);

  if (token) {
    return <Navigate to="/" />;
  }

  return (
    <div className="login-page">
      <div className="login-card">
        {fromLogout && (
          <div className="login-banner" role="status">
            You have been logged out successfully.
          </div>
        )}

        <div className="login-card__brand">
          {!fromLogout && (
            <img
              src={getLogoImage()}
              alt="Logo"
              className="login-card__logo"
            />
          )}
          <h1>Welcome back</h1>
          <p>Sign in with your client ID to continue</p>
        </div>

        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label htmlFor="username">Client ID</label>
            <div className="login-input-wrap">
              <PersonIcon />
              <input
                type="text"
                id="username"
                inputMode="numeric"
                autoComplete="username"
                placeholder="Enter your client ID"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="password">Password</label>
            <div className="login-input-wrap">
              <LockIcon />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
              <button
                type="button"
                className="login-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
          </div>

          <button type="submit" className="login-submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {showRegisterLink(hostname) && (
          <p className="login-footer">
            Don&apos;t have an account?{' '}
            <Link to="/register">Create one</Link>
          </p>
        )}

        <p className="login-card__tagline">
          Secure trading platform
        </p>
      </div>
    </div>
  );
};

export default Login;
